// material-ui
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';

// assets
import { CheckCircleOutlined, ClockCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { scopes } from 'config/scopes';

const formatDate = (value) => {
  if(!value) return null;
  try {
    return format(parseISO(value), 'yyyy-MM-dd HH:mm');
  }
  catch(e) {
    return value;
  }
}

// instalment stage (MitigateInstalmentStage) -> label key + chip colour
const STAGES = {
  NONE: {key: 'instalment_stage_none', color: 'default'},
  KYC: {key: 'instalment_stage_kyc', color: 'info'},
  SIGNATURE: {key: 'instalment_stage_signature', color: 'info'},
  PAYMENT: {key: 'instalment_stage_payment', color: 'warning'},
  READY_FOR_ACTIVATION: {key: 'instalment_stage_ready_for_activation', color: 'warning'},
  ACTIVE: {key: 'instalment_stage_active', color: 'success'}
};

const ACTION_ACTIVATE = 'activate';

/**
 * Instalment activation card of the client (FULL_FLOW partners): the current instalment stage and the two conditions
 * after the signed contract package: "first instalment received" (read only here: the client pays to Cygnus, so Cygnus
 * sets it) and "service delivered / activation" (partner action, scope mitigate_activate_client). The deal becomes
 * ACTIVE when both are done (decided by the backend, no job involved).
 */
export default({model, clientId, personal}) => {
  const dispatcher = useDispatch();
  const [t, i18n] = useTranslation();
  const [confirmAction, setConfirmAction] = useState(null);
  const [buttonEnabled, setButtonEnabled] = useState(true);

  const stage = personal?.instalmentStage ? personal.instalmentStage : 'NONE';
  const stageInfo = STAGES[stage] ? STAGES[stage] : STAGES.NONE;
  const activationAvailable = personal?.activationAvailable === true;
  const firstPaymentReceived = !!personal?.firstPaymentReceived;
  const serviceActivated = !!personal?.serviceActivated;
  const firstPaymentAt = formatDate(personal?.firstPaymentReceived);
  const serviceActivatedAt = formatDate(personal?.serviceActivated);
  const activeAt = formatDate(personal?.instalmentActive);
  const isActive = stage === 'ACTIVE' || !!personal?.instalmentActive;

  const canActivate = model.hasScope(scopes.SCOPE_MITIGATE_ACTIVATE_CLIENT) === true && activationAvailable && !serviceActivated;

  const runAction = () => {
    if(!clientId || !confirmAction) {
      return;
    }
    const successKey = 'instalment_activate_success';
    const errorKey = 'instalment_activate_error';
    setButtonEnabled(false);
    const request = model.activateClient(dispatcher, clientId);
    request.unwrap().then(res => {
      setButtonEnabled(true);
      setConfirmAction(null);
      if(res?.success) {
        dispatcher(openSnackbar({open: true, message: t(successKey), variant: 'alert', alert: {color: 'success'}}));
        // refresh the client details so the stage / timestamps are up to date
        model.fetchDetails(dispatcher, clientId);
      }
      else {
        dispatcher(openSnackbar({open: true, message: t(errorKey), variant: 'alert', alert: {color: 'error'}}));
      }
    }, e => {
      setButtonEnabled(true);
      setConfirmAction(null);
      dispatcher(openSnackbar({open: true, message: t(errorKey), variant: 'alert', alert: {color: 'error'}}));
    });
  }

  const renderRow = (done, title, doneLabel, actionAllowed, actionLabel, actionIcon, action) => (
    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
      {done ?
        <CheckCircleOutlined style={{fontSize: '130%', color: '#52c41a'}} /> :
        <ClockCircleOutlined style={{fontSize: '130%', color: '#8c8c8c'}} />
      }
      <Typography variant="subtitle1">{title}</Typography>
      {done ?
        <Chip label={doneLabel} color="success" size="small" /> :
        <Chip label={t('instalment_pending')} size="small" variant="outlined" />
      }
      {actionAllowed &&
        <Button size="small" variant="shadow" color="info" disabled={!buttonEnabled} startIcon={actionIcon} onClick={() => setConfirmAction(action)}>
          {actionLabel}
        </Button>
      }
    </Stack>
  );

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MainCard title={t('instalment_activation')} sx={{background: isActive ? '#EDFFEF' : '#F4F8FF'}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <RocketOutlined style={{ fontSize: '150%'}} />
                <Typography variant="caption">{t('instalment_stage')}:</Typography>
                <Chip label={t(stageInfo.key)} color={stageInfo.color} size="small" variant={stageInfo.color === 'default' ? 'outlined' : 'filled'} />
              </Stack>
              <Typography sx={{marginTop: 2}}>{t('instalment_desc')}</Typography>
              <Stack spacing={1.5} sx={{marginTop: 2}}>
                {renderRow(
                  firstPaymentReceived,
                  t('instalment_first_payment'),
                  t('instalment_first_payment_at', {date: firstPaymentAt}),
                  false,
                  null,
                  null,
                  null
                )}
                {!firstPaymentReceived &&
                  <Typography variant="caption" sx={{marginTop: '-4px !important', paddingLeft: '32px'}}>{t('instalment_first_payment_hint')}</Typography>
                }
                {renderRow(
                  serviceActivated,
                  t('instalment_service_activated'),
                  t('instalment_service_activated_at', {date: serviceActivatedAt}),
                  canActivate,
                  t('instalment_activate_button'),
                  <CheckCircleOutlined />,
                  ACTION_ACTIVATE
                )}
              </Stack>
              {isActive &&
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{marginTop: 2}}>
                  <Chip label={activeAt ? t('instalment_active_at', {date: activeAt}) : t('instalment_active')} color="success" />
                </Stack>
              }
              {!isActive && !activationAvailable &&
                <Typography variant="caption" sx={{display: 'block', marginTop: 2}}>{t('instalment_not_available')}</Typography>
              }
            </MainCard>
          </Grid>
        </Grid>
      </Grid>
      <Dialog maxWidth="sm" fullWidth onClose={() => setConfirmAction(null)} open={confirmAction !== null} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        <DialogTitle>{t('instalment_activate_confirm_title')}</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          <Typography>{t('instalment_activate_confirm_desc')}</Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item></Grid>
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button color="error" onClick={() => setConfirmAction(null)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!buttonEnabled} onClick={runAction}>
                  {t('instalment_confirm_button')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
}
